import { useCallback, useMemo, useState } from 'react';
import {
  AppointmentStatusRequired,
  AttributeType,
  RoiSmsData,
  RoiType,
} from '@weave/schema-gen-ts/dist/schemas/analytics-reporting/analytics_gateway/analytics_gateway.pb';
import { LocationChip, UserCard } from '@frontend/analytics-shared';
import { getDisplayName } from '@frontend/get-display-name';
import { useTranslation } from '@frontend/i18n';
import { formatPhoneNumber } from '@frontend/phone-numbers';
import { useAppScopeStore, useScopedAppFlagStore } from '@frontend/scope';
import { formatters } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { Chip, ChipVariants, SortingValue, Table, TableColumnConfig, Tabs } from '@frontend/design-system';
import { featureFlags } from '../../../../feature-flags';
import { TableActions } from '../../../table-actions';
import { useFetchRoiPatients } from '../../hooks/use-fetch-roi-patients';
import { Sorting } from '../../types';
import { useARRoiShallowStore } from '../hooks/use-ar-roi-store';

interface ARRoiTableProps {
  trackingIdBase?: string;
}

type Tab = 'opportunities' | 'all';

interface TablePanelProps extends ARRoiTableProps {
  tableInstanceId: string;
  tabType: Tab;
}

const TablePanel = ({ tableInstanceId, tabType, trackingIdBase = '' }: TablePanelProps) => {
  const { t } = useTranslation('analytics');
  const { filters } = useARRoiShallowStore('filters');
  const { accessibleLocationData } = useAppScopeStore();
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const isDemoAccount = getFeatureFlagValue(featureFlags.appointmentRemindersRoiDemoData);

  const [sorting, setSorting] = useState<Sorting>({
    orderBy: 'desc',
    orderByField: 'appointment_date',
  });

  const { hasNextPage, hasPreviousPage, isLoading, pageConfig, patients, setPageConfig } = useFetchRoiPatients({
    payload: {
      attributeType: AttributeType.FUTURE_APPOINTMENTS,
      roiType: RoiType.APPOINTMENT_REMINDERS,
      smsRequest: {
        ...sorting,
        appointmentFilters: {
          appointmentFilterFlag: tabType === 'opportunities',
          appointmentStatus: {
            appointmentStatus: AppointmentStatusRequired.APPOINTMENT_CONFIRMED,
            appointmentStatusEquality: false,
          },
        },
        endDate: filters?.endDate ?? '',
        locationId: filters?.locationIds,
        startDate: filters?.startDate ?? '',
      },
    },
    isDemoAccount,
  });

  const handleSorting = useCallback((sortValues: SortingValue<string>[]) => {
    const { id, value } = sortValues[0] || {};
    if (id && value) {
      setSorting({
        orderByField: id,
        orderBy: value,
      });
    }
  }, []);

  const columnConfig: TableColumnConfig<RoiSmsData>[] = useMemo(() => {
    return [
      {
        accessor: (patient) => patient,
        cellRenderer: ({ locationId, patientId, firstName = '', lastName = '', preferredName = '' }: RoiSmsData) => {
          const { firstName: givenName, lastName: familyName } = getDisplayName({ firstName, lastName, preferredName });
          return (
            <UserCard
              firstName={givenName}
              key={patientId}
              lastName={familyName}
              locationId={locationId}
              showOnlyName
              userId={patientId}
            />
          );
        },
        disableColumnFilter: true,
        disableSortBy: true,
        Header: t('Patient Name'),
        id: 'patient_name',
        sticky: 'left',
        width: 200,
      },
      {
        accessor: ({ locationId }) => locationId,
        cellRenderer: (value: string) => {
          return value ? (
            <LocationChip locationName={accessibleLocationData?.[value]?.name || value} maxWidth={180} />
          ) : (
            '-'
          );
        },
        disableSortBy: true,
        Header: t('Location'),
        id: 'location_id',
        omit: filters?.locationIds?.length === 1,
        width: 220,
      },
      {
        accessor: ({ status }) => status,
        cellRenderer: (status: RoiSmsData['status']) => {
          if (!status) {
            return '-';
          }

          let chipVarient: ChipVariants = 'primary';

          switch (status) {
            case 'Completed':
              chipVarient = 'seaweed';
              break;

            case 'Confirmed':
            case 'Scheduled':
              chipVarient = 'success';
              break;

            case 'Unconfirmed':
              chipVarient = 'critical';
              break;
          }

          return <Chip variant={chipVarient}>{status}</Chip>;
        },
        disableSortBy: true,
        Header: t('Appointment Status'),
        id: 'status',
        width: 180,
      },
      {
        accessor: ({ apptsCompletionDate }) => (apptsCompletionDate ? formatters.date(apptsCompletionDate) : '-'),
        Header: t('Appointment Date'),
        id: 'appts_completion_date',
        width: 180,
      },
      {
        accessor: ({ phoneNumber }) => (phoneNumber ? formatPhoneNumber(phoneNumber) : '-'),
        disableSortBy: true,
        Header: t('Patients Number'),
        id: 'phone_number',
        width: 160,
      },
      {
        accessor: ({ appointmentType }) => appointmentType,
        disableSortBy: true,
        Header: t('Contact Type'),
        id: 'appointment_type',
        width: 160,
      },
      {
        accessor: ({ patientId, patientName, phoneNumber }) => ({
          patientId,
          patientName,
          phoneNumber,
        }),
        cellRenderer: ({ patientId, patientName, phoneNumber }) => {
          return (
            <TableActions
              personId={patientId}
              personName={patientName}
              phoneNumber={phoneNumber}
              trackingIdBase={`${trackingIdBase}-${tabType}`}
            />
          );
        },
        cellAlign: 'center',
        disableSortBy: true,
        Header: '',
        id: 'actions',
        sticky: 'right',
        width: 120,
      },
    ];
  }, [accessibleLocationData]);

  return (
    <Table
      colConfig={columnConfig}
      data={patients}
      disableMultiSort
      hasResizeColumns
      hasResponsiveColWidths
      isLoading={isLoading}
      isPaginated
      manualPaginationConfig={{
        handleChange: (action: 'next' | 'prev') => {
          if (action === 'next') {
            setPageConfig({ ...pageConfig, pageNumber: pageConfig.pageNumber + 1 });
          } else {
            setPageConfig({ ...pageConfig, pageNumber: pageConfig.pageNumber - 1 });
          }
        },
        hasNext: hasNextPage,
        hasPrevious: hasPreviousPage,
        page: pageConfig.pageNumber,
        defaultRowsPerPage: pageConfig.pageSize,
        onNumRowsChange: (num) => {
          setPageConfig({ pageNumber: 1, pageSize: num });
        },
        rowsPerPageOptions: [10, 25, 50, 100],
      }}
      manualSortBy
      onSortChange={handleSorting}
      tableInstanceId={tableInstanceId}
    />
  );
};

export const ARRoiTable = (props: ARRoiTableProps) => {
  const { t } = useTranslation('analytics');
  const [activeTab, setActiveTab] = useState<Tab>('opportunities');

  return (
    <div>
      <Tabs initialTab={activeTab} onChange={(id: string) => setActiveTab(id as Tab)}>
        <Tabs.Bar css={{ marginBottom: theme.spacing(2) }}>
          <Tabs.Tab id='opportunities' key='opportunities' trackingId={`${props.trackingIdBase}-opportunities`}>
            {t('Opportunities')}
          </Tabs.Tab>
          <Tabs.Tab id='all' key='all' trackingId={`${props.trackingIdBase}-all`}>
            {t('All')}
          </Tabs.Tab>
        </Tabs.Bar>

        <Tabs.Panel controller='opportunities'>
          <TablePanel {...props} tableInstanceId='ar-roi-opportunities-patients' tabType='opportunities' />
        </Tabs.Panel>
        <Tabs.Panel controller='all'>
          <TablePanel {...props} tableInstanceId='ar-roi-all-patients' tabType='all' />
        </Tabs.Panel>
      </Tabs>
    </div>
  );
};
